import { Box, FormControlLabel, FormGroup, Radio } from '@mui/material';
import { DatePicker, LocalizationProvider, TimePicker } from '@mui/x-date-pickers-pro';
import { renderTimeViewClock } from '@mui/x-date-pickers/timeViewRenderers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { FastField } from 'formik';
import moment from 'moment';
import dayjs, { Dayjs } from 'dayjs';
import { useEffect, useMemo, useState } from 'react';

import { getMoment } from '@shared/commonFunctions';
import { CheckboxField } from '@shared/fields/CheckboxField';
import { SelectAutocompleteField } from '@shared/fields/SelectAutocompleteField';
import { CancelButton, SaveButton } from '@shared/inputs/Buttons';
import { dateFormat, dateFormatWithTime } from '@shared/constants/formats';
import { AddReminderLabel, CommonButtonWrapper, TypographyAddReminder } from '@styles/CommonStyles';
import { FormRow } from '@styles/StyledDiv';

import { useUserAccount } from '@shared/hooks/useUserAccount';
import { SelectAutocomplete } from '@shared/inputs/SelectAutocomplete';
import { IReminderFormProps } from '../interfaces';

export const AddReminderForm = ({ deals = [], users = [], handleClose, setFieldValue }: IReminderFormProps) => {
  const { userId } = useUserAccount();
  const [reminderAgent, setReminderAgent] = useState<string>(userId ?? '');
  const [value, setValue] = useState<Dayjs | null>(null);
  const [checkboxesState, setCheckboxesState] = useState<string>('-1');
  const handleTimeAddition = (e: React.ChangeEvent<HTMLInputElement>) => {
    let date: Date | string = new Date();
    date = date.toISOString();
    const unit = e?.target?.name?.split('-')[1] as moment.DurationInputArg2;
    const day = e?.target?.name.split('-')[0] as moment.DurationInputArg1;
    const newTime = getMoment(date).add(day, unit);

    setValue(dayjs(newTime.format(dateFormatWithTime)));
    setCheckboxesState(e?.target?.name);
    setFieldValue?.('reminderDate', newTime.toISOString());
  };

  const dealsOptions = useMemo(
    () =>
      deals?.map((deal) => ({
        value: deal?.dealId,
        label: `[${deal?.dealNumericId || ''}] - ${deal?.lenderName || ''} - ${deal?.assetTag || ''}`,
      })) ?? [],
    [deals]
  );
  
  const userSelectOptions = useMemo(
    () =>
      users?.map((user) => ({
        value: user.id,
        label: `${user?.firstName} ${user?.lastName} (${user?.email})`,
      })) || [],
    [users]
  );

  useEffect(() => {
    setFieldValue?.('userId', reminderAgent);
  }, [reminderAgent]);
  
  return (
    <>
      <FormRow>
        <TypographyAddReminder> Agent </TypographyAddReminder>
        <Box style={{ width: '70%' }}>
          <SelectAutocomplete
            removeExtraPadding
            id="userId"
            value={reminderAgent}
            placeholder="Select Agent"
            options={userSelectOptions}
            onChange={(userId: string) => setReminderAgent(userId)}
          />
        </Box>
      </FormRow>
      <FormRow>
        <TypographyAddReminder> Date & Time </TypographyAddReminder>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Box style={{ width: '44%' }}>
            <DatePicker
              label="Date"
              format={dateFormat}
              value={value ? dayjs(value) : null}
              onChange={(val: Dayjs | null) => {
                setCheckboxesState('-1');
                setValue(val);
                setFieldValue && setFieldValue('reminderDate', val);
              }}
            />
          </Box>
          <Box style={{ width: '27%' }}>
            <TimePicker
              label="Time"
              value={value ? dayjs(value) : null}
              viewRenderers={{
                hours: renderTimeViewClock,
                minutes: renderTimeViewClock,
                seconds: renderTimeViewClock,
              }}
              onChange={(val: Dayjs | null) => {
                setCheckboxesState('-1');
                setValue(val);
                setFieldValue && setFieldValue('reminderDate', val);
              }}
            />
          </Box>
        </LocalizationProvider>
      </FormRow>
      <FormRow>
        <AddReminderLabel> Quick Options </AddReminderLabel>

        <Box style={{ width: '70%' }}>
          <Box style={{ width: '33%', display: 'inline-block' }}>
            <FormGroup>
              <FormControlLabel
                label="10mins"
                control={
                  <Radio
                    checked={checkboxesState === '10-minutes'}
                    color="primary"
                    name={'10-minutes'}
                    onChange={handleTimeAddition}
                  />
                }
              />
              <FormControlLabel
                label="1hr"
                control={
                  <Radio
                    checked={checkboxesState === '60-minutes'}
                    color="primary"
                    name={'60-minutes'}
                    onChange={handleTimeAddition}
                  />
                }
              />
              <FormControlLabel
                label="1day"
                control={
                  <Radio
                    checked={checkboxesState === '1-days'}
                    color="primary"
                    name={'1-days'}
                    onChange={handleTimeAddition}
                  />
                }
              />
              <FormControlLabel
                label="1month"
                control={
                  <Radio
                    checked={checkboxesState === '30-days'}
                    color="primary"
                    name={'30-days'}
                    onChange={handleTimeAddition}
                  />
                }
              />
            </FormGroup>
          </Box>
          <Box style={{ width: '33%', display: 'inline-block' }}>
            <FormGroup>
              <FormControlLabel
                label="20mins"
                control={
                  <Radio
                    checked={checkboxesState === '20-minutes'}
                    color="primary"
                    name={'20-minutes'}
                    onChange={handleTimeAddition}
                  />
                }
              />
              <FormControlLabel
                label="3hrs"
                control={
                  <Radio
                    checked={checkboxesState === '180-minutes'}
                    color="primary"
                    name={'180-minutes'}
                    onChange={handleTimeAddition}
                  />
                }
              />
              <FormControlLabel
                label="1week"
                control={
                  <Radio
                    checked={checkboxesState === '7-days'}
                    color="primary"
                    name={'7-days'}
                    onChange={handleTimeAddition}
                  />
                }
              />
              <FormControlLabel
                label="3months"
                control={
                  <Radio
                    checked={checkboxesState === '90-days'}
                    color="primary"
                    name={'90-days'}
                    onChange={handleTimeAddition}
                  />
                }
              />
            </FormGroup>
          </Box>
          <Box style={{ width: '33%', display: 'inline-block' }}>
            <FormGroup>
              <FormControlLabel
                label="30mins"
                control={
                  <Radio
                    checked={checkboxesState === '30-minutes'}
                    color="primary"
                    name={'30-minutes'}
                    onChange={handleTimeAddition}
                  />
                }
              />
              <FormControlLabel
                label="6hrs"
                control={
                  <Radio
                    checked={checkboxesState === '360-minutes'}
                    color="primary"
                    name={'360-minutes'}
                    onChange={handleTimeAddition}
                  />
                }
              />
              <FormControlLabel
                label="2weeks"
                control={
                  <Radio
                    checked={checkboxesState === '14-days'}
                    color="primary"
                    name={'14-days'}
                    onChange={handleTimeAddition}
                  />
                }
              />
              <FormControlLabel
                label="6months"
                control={
                  <Radio
                    checked={checkboxesState === '180-days'}
                    color="primary"
                    name={'180-days'}
                    onChange={handleTimeAddition}
                  />
                }
              />
            </FormGroup>
          </Box>
        </Box>
      </FormRow>
      <FormRow>
        <TypographyAddReminder> Email Reminder </TypographyAddReminder>
        <Box style={{ width: '70%' }}>
          <FastField
            component={CheckboxField}
            name="isEmailReminder"
          />
        </Box>
      </FormRow>
      <FormRow>
        <TypographyAddReminder> Note </TypographyAddReminder>
        <Box style={{ width: '70%' }}>
          <FastField
            name="description"
            component={'textarea'}
            rows="10"
            cols="50"
            resize="none"
          />
        </Box>
      </FormRow>
      <FormRow>
        <TypographyAddReminder> Deal </TypographyAddReminder>
        <Box style={{ width: '70%' }}>
          <FastField
            name="dealId"
            component={SelectAutocompleteField}
            label="Select Deal"
            placeholder="Select Deal"
            options={dealsOptions}
          />
        </Box>
      </FormRow>

      <CommonButtonWrapper
        mt="20px"
        gap={1.2}
      >
        <SaveButton
          label="Submit"
          width="default"
        />
        <CancelButton
          variant="outlined"
          width="default"
          onClick={handleClose}
        />
      </CommonButtonWrapper>
    </>
  );
};
